import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PropertyActions } from '@galo-monitor/stores/property'
import DataTable from '@galo-monitor/web-components/DataTable'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal/ErrorModal'
import PropertyModal from '@galo-monitor/web-containers/modals/farm/PropertyModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const PropertiesScreen = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const userActions = useMemo(
    () => getActionsBySlug(routes.properties.slug),
    [getActionsBySlug, routes.properties.slug]
  )

  const { data: properties, mutate, isValidating } = useGaloMonitorApi('/farms/properties')

  const columns = useMemo(
    () => [
      {
        title: t('name of {this}', { this: t('property', { howMany: 1 }), howMany: 1, gender: 'male' }),
        field: 'name'
      },
      {
        title: t('city', { howMany: 1 }),
        field: 'city'
      },
      {
        title: t('state', { howMany: 1 }),
        field: 'state'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-farm',
        Component: PropertyModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, property) => {
      createDialog({
        id: 'edit-plan',
        Component: PropertyModal,
        props: {
          property,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, property) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('property', { howMany: 1 }),
            name: property.name
          }),
          onConfirm: () => {
            dispatch(PropertyActions.removeProperty(
              property,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('property', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'module-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('property', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('you need detach all farms accounts to delete this plan', {
                      this: t('plan', { howMany: 1 }),
                      action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.properties.title,
      key: 'farm-properties-screen',
      url: '/properties',
      active: true
    }
  ], [routes.properties.title])

  return (
    <Screen title={ routes.properties.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ properties }
        columns={ columns }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
      />
    </Screen>
  )
}

export default PropertiesScreen
